import { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { FormulaTable } from '../FormulaTable';
import { TableRowProduct } from '../TableRowProduct';
import { SlideInModalDialogStyled } from '@/components';
import {
  convertFormRecordToMenuRecipeOverrideUpdateArray,
  MenuRecipeOverrideUpdate,
  onQueryError,
  onQuerySuccess,
  QuizAnswer,
  RecipeItem,
  useMenu,
} from '@/core';

type DialogEditProductsProps = {
  answer: QuizAnswer;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export type ProductToBeModified = {
  id: number;
  amount_change: number;
};

export type DialogEditProductsFormData = {
  productsToBeRemoved: number[];
  productsToBeModified: Record<string, { amount: string }>;
};

const schema = z.object({
  productsToBeRemoved: z.array(z.number()),
  productsToBeModified: z.record(z.string(), z.object({ amount: z.string() })),
});

export const DialogEditProducts = ({ answer, open, setOpen }: DialogEditProductsProps) => {
  const [addRecipeItemsState, setAddRecipeItemsState] = useState<RecipeItem[]>([]);
  const {
    queries: { useMenuRecipeOverrides, useMenuRecipe },
    mutations: { updateMenuRecipeOverrideMutation },
  } = useMenu();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: recipeItemsResponse } = useMenuRecipe(Number(id), open);
  const { data: initialActions } = useMenuRecipeOverrides(answer.id);
  const hasInitialized = useRef(false);
  const wasProductAdded = useMemo(() => addRecipeItemsState.length > 0, [addRecipeItemsState]);

  const methods = useForm<DialogEditProductsFormData>({
    defaultValues: {
      productsToBeRemoved: [],
      productsToBeModified: {},
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    // Initialize form with initial values (actions)
    if (recipeItemsResponse?.data && initialActions?.data && !hasInitialized.current) {
      methods.reset({
        productsToBeRemoved: initialActions.data
          .filter((o) => o.amount_change === null)
          .map((o) => o.product.id),
        productsToBeModified: recipeItemsResponse.data.reduce(
          (acc, recipeItem) => {
            const existing = initialActions.data.find(
              (a) => a.product.id === recipeItem.product.id,
            );
            acc[recipeItem.product.id] = {
              amount:
                existing?.amount_change === null
                  ? '0'
                  : String(Number(recipeItem.amount) + Number(existing?.amount_change ?? 0)),
            };
            return acc;
          },
          {} as Record<string, { amount: string }>,
        ),
      });

      setAddRecipeItemsState(
        initialActions.data
          .filter((o) => {
            const productIds = recipeItemsResponse.data.map((ir) => ir.product.id);
            return o.amount_change !== null && !productIds.includes(o.product.id);
          })
          .map((o) => ({
            product: {
              id: o.product.id,
              name: o.product.name,
            },
            amount: o.amount_change ?? 0,
          })),
      );
      hasInitialized.current = true;
    }
  }, [recipeItemsResponse?.data, initialActions?.data, methods.reset, methods]);

  const { getValues } = methods;

  const onSubmit = async (data: DialogEditProductsFormData) => {
    const modifiedArray: MenuRecipeOverrideUpdate[] =
      convertFormRecordToMenuRecipeOverrideUpdateArray(
        data.productsToBeModified,
        recipeItemsResponse?.data ?? [],
      );

    const productsToBeRemovedApiForm: MenuRecipeOverrideUpdate[] = data.productsToBeRemoved.map(
      (productId) => ({
        product_id: productId,
        amount_change: null,
      }),
    );

    const modifiedData: MenuRecipeOverrideUpdate[] = modifiedArray.concat(
      productsToBeRemovedApiForm,
    );

    addRecipeItemsState.forEach((recipeItem) => {
      if (recipeItem.amount === null) return;
      if (!Number(recipeItem.amount)) return;
      modifiedData.push({
        amount_change: Number(recipeItem.amount),
        product_id: recipeItem.product.id,
      });
    });

    const updateResponse = await updateMenuRecipeOverrideMutation.mutateAsync({
      answerId: answer.id,
      data: modifiedData,
    });
    if (updateResponse.status === 200 && updateResponse.data) {
      onQuerySuccess({
        queryKeysToInvalidate: [['menuRecipeOverrides', answer.id]],
        successMessage: t('VARIANT.RECIPE_OVERRIDE_UPDATE_SUCCESS'),
        queryClient,
        callback: () => {
          setOpen(false);
        },
      });
    } else {
      onQueryError({ errorMessage: t('VARIANT.RECIPE_OVERRIDE_UPDATE_FAILURE') });
    }
  };

  return (
    <FormProvider {...methods}>
      <SlideInModalDialogStyled
        open={open}
        setOpen={setOpen}
        title={t('VARIANT.RECIPE_OVERRIDE_TITLE_MENU')}
        onActionButtonClick={() => onSubmit(getValues())}
        actionButtonLabel={t('GLOBAL.SAVE')}
      >
        <Stack direction="column" gap={3}>
          <Stack direction="column" gap={1}>
            <Typography variant="body1" color="text.secondary">
              {t('VARIANT.RECIPE_OVERRIDE_MODIFY_MENU')}
            </Typography>
            <Card>
              {recipeItemsResponse?.data && recipeItemsResponse.data.length > 0 ? (
                <>
                  <Typography variant="body2" m={'12px 0 12px 110px'} fontWeight={500}>
                    {t('MENU.PRODUCTS')}
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '1%' }}></TableCell>
                        <TableCell>{t('HEADERS.NAME')}</TableCell>
                        <TableCell sx={{ width: '17%' }}></TableCell>
                        <TableCell sx={{ width: '20%' }}>{t('ORDER.COUNT')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ '.MuiTableCell-root': { py: 1 } }}>
                      {recipeItemsResponse?.data.map((recipeItem) => (
                        <TableRowProduct key={recipeItem.product.id} recipeItem={recipeItem} />
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <Typography variant="body2" my={2} fontWeight={500}>
                  {t('PRODUCT.NO_INGREDIENTS')}
                </Typography>
              )}
            </Card>
          </Stack>
          <Stack direction="column" gap={1}>
            <Typography variant="body1" color="text.secondary">
              {t('VARIANT.RECIPE_OVERRIDE_ADD_MENU')}
            </Typography>
            <Card>
              {wasProductAdded && (
                <Typography variant="body2" m={'12px 0 12px 110px'} fontWeight={500}>
                  {t('MENU.PRODUCTS')}
                </Typography>
              )}
              <FormulaTable
                recipeItems={addRecipeItemsState}
                setRecipeItems={setAddRecipeItemsState}
              />
            </Card>
          </Stack>
        </Stack>
      </SlideInModalDialogStyled>
    </FormProvider>
  );
};
